import React, {ReactElement, useRef} from 'react';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {getAnswerColors} from '../common/utils';
import Button from '../../../../../incase-domain/components/button/Button';
import AnswerComment from './AnswerComment';

export enum AnswerTypes {
    SELECTED = 'marked',
    CORRECT = 'correct',
    INCORRECT = 'incorrect',
    DEBATABLE = 'debatable',
    NONE = 'none',
}

interface QuestionAnswerProps {
    answer: string;
    answerType: AnswerTypes;
    comment?: string;
    leadingIcon?: string;
    onClick?: () => void;
    correctColor?: string;
    incorrectColor?: string;
    debatableColor?: string;
    writeAnswerIndices?: boolean;
    index?: number;
    draggable?: boolean;
    styles?: React.CSSProperties;
    disabled?: boolean;
    device?: DeviceType;
    showComment?: boolean;
    setShowComment: (show: boolean) => void;
    hostStyled?: boolean;
    selected?: boolean;
}

function QuestionAnswer({
    answer,
    answerType,
    comment,
    leadingIcon,
    onClick,
    correctColor,
    incorrectColor,
    debatableColor,
    draggable = false,
    writeAnswerIndices,
    index,
    styles,
    disabled = false,
    device = DeviceType.DESKTOP,
    showComment = false,
    setShowComment,
    hostStyled = false,
    selected = false,
}: QuestionAnswerProps): ReactElement {
    const questionAnswerRef = useRef<HTMLDivElement>(null);

    const {getBtnAnswerColor, getCustomAnswerColor, getCustomAnswerColorWithAlpha} = getAnswerColors(
        answerType,
        hostStyled ? undefined : correctColor,
        hostStyled ? undefined : incorrectColor,
        hostStyled ? undefined : debatableColor
    );

    function getAnswerText(): string {
        if (index === undefined) return answer;
        return writeAnswerIndices ? `${String.fromCharCode('A'.charCodeAt(0) + index)}: ${answer}` : answer;
    }

    return (
        <div
            ref={questionAnswerRef}
            className={`c-questionElement__answer ${showComment ? 'has-comment-open' : ''} ${selected ? 'is-selected' : ''} ${
                hostStyled && answerType !== AnswerTypes.NONE ? `is-${answerType}` : ''
            }`}
        >
            <Button
                variant="soft"
                color={getBtnAnswerColor()}
                customColor={hostStyled ? undefined : getCustomAnswerColorWithAlpha(0.35)}
                customIconColor={hostStyled ? undefined : getCustomAnswerColor()}
                size="md"
                leading={leadingIcon}
                align="left"
                onClick={() => {
                    onClick?.();
                }}
                dynamicSize
                disablePointerEvents={draggable}
                disabled={disabled}
            >
                <div style={hostStyled ? undefined : styles}>{getAnswerText()}</div>
            </Button>

            {comment && (
                <AnswerComment
                    answer={getAnswerText()}
                    comment={comment}
                    showComment={showComment}
                    setShowComment={setShowComment}
                    anchorRef={questionAnswerRef}
                    device={device}
                />
            )}
        </div>
    );
}

export default QuestionAnswer;
